import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CopyableValue } from "@/components/CopyableValue";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, type ContainerRow } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";

// Narrow ContainerRow slice the form actually reads. Onboarding doesn't
// have a fully-hydrated row to pass, and the dashboard's IntegrationsTab
// passes the whole thing — Pick keeps both call sites honest.
type GchatContainer = Pick<ContainerRow, "id" | "deploymentStatus" | "customDomainFqdn">;

// Step-by-step Cloud Console instructions + credential upload form for a
// customer-owned Google Chat app. The customer follows the steps in their
// own Cloud Console, then pastes the service account JSON + project number
// here. We forward to the agent, which validates inbound JWTs and posts
// outbound Chat API calls directly using those credentials.
export function CustomGchatInstructions({
  container,
  onConnected,
}: {
  container: GchatContainer;
  onConnected?: () => void;
}) {
  const queryClient = useQueryClient();
  const [serviceAccountJson, setServiceAccountJson] = useState("");
  const [projectNumber, setProjectNumber] = useState("");
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Accept the customer's service-account JSON file directly — pasting can
  // introduce trailing whitespace, smart-quote substitutions, or partial
  // selections that the backend would reject. Reading the file as text and
  // parsing it locally catches those issues before the request fires.
  function handleSaFileUpload(file: File) {
    setUploadError(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setUploadError("Couldn't read that file. Try again or paste the JSON manually.");
    };
    reader.onload = () => {
      const raw = String(reader.result ?? "");
      try {
        const parsed = JSON.parse(raw) as { client_email?: string; private_key?: string };
        if (!parsed.client_email || !parsed.private_key) {
          setUploadError(
            "That doesn't look like a service account JSON — missing client_email or private_key.",
          );
          return;
        }
      } catch {
        setUploadError("That file isn't valid JSON.");
        return;
      }
      setServiceAccountJson(raw);
    };
    reader.readAsText(file);
  }

  const statusQuery = useQuery({
    queryKey: ["gchat-custom-status", container.id],
    queryFn: () => api.gchat.custom.status(container.id),
    enabled: container.deploymentStatus === "running",
  });

  const install = useMutation({
    mutationFn: () =>
      api.gchat.custom.install(container.id, serviceAccountJson.trim(), projectNumber.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gchat-custom-status", container.id] });
      setServiceAccountJson("");
      setProjectNumber("");
      onConnected?.();
    },
  });

  const disconnect = useMutation({
    mutationFn: () => api.gchat.custom.disconnect(container.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gchat-custom-status", container.id] });
      setDisconnectOpen(false);
    },
  });

  const connected = statusQuery.data?.connected === true;
  // Backend picks the preferred URL — custom Railway domain if attached,
  // otherwise the .up.railway.app default. Fall back to the Boo's branded
  // domain while the status query is loading; the raw service URL is no
  // longer sent to the dashboard.
  const eventsUrl =
    statusQuery.data?.eventsUrl ??
    (container.customDomainFqdn ? `https://${container.customDomainFqdn}/gchat-custom/events` : null);

  // ── Connected state ──────────────────────────────────────────────────
  if (connected) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-emerald-50 ring-1 ring-emerald-200">
              <Check className="size-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-sm font-medium">
                Google Chat — connected
                {statusQuery.data?.customerName ? ` to ${statusQuery.data.customerName}` : ""}
              </div>
              <div className="text-xs text-muted-foreground">
                Using your own Google Chat app and service account.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={disconnect.isPending}
            onClick={() => setDisconnectOpen(true)}
          >
            {disconnect.isPending && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
            Disconnect
          </Button>
        </div>
        {disconnect.isError && <InlineError error={disconnect.error} />}
        <ConfirmDialog
          open={disconnectOpen}
          onOpenChange={setDisconnectOpen}
          title="Disconnect Custom Google Chat?"
          description="Clears the Boo's stored Google Chat credentials. Your Chat app in Google Cloud is not deleted — you can re-paste credentials any time to reconnect."
          confirmLabel="Disconnect"
          loading={disconnect.isPending}
          onConfirm={() => disconnect.mutate()}
        />
      </div>
    );
  }

  const canSubmit = serviceAccountJson.trim().length > 0 && projectNumber.trim().length > 0;

  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground">
        Google Chat doesn't expose an API for creating apps in your workspace, so the steps below run in your own Google Cloud Console.
      </p>

      <ol className="space-y-3 text-sm list-decimal pl-5 text-foreground">
        <li>
          <span className="font-medium">Create a Google Cloud project</span>{" "}
          under your Workspace org. Note the <strong>Project Number</strong> — you'll paste it below.
        </li>
        <li>
          <span className="font-medium">Enable the Google Chat API</span> in that project (APIs &amp; Services → Library → search "Google Chat API" → Enable).
        </li>
        <li>
          <span className="font-medium">Create a service account</span> (IAM &amp; Admin → Service Accounts → Create). Open it →{" "}
          <strong>Keys</strong> tab → <strong>Add Key → Create new key → JSON</strong>. Save the downloaded JSON — you'll paste it below.
          <p className="text-xs text-muted-foreground mt-1">
            If you see "Service account key creation is disabled", a Workspace admin needs to disable the{" "}
            <code className="text-xs">iam.disableServiceAccountKeyCreation</code> org policy temporarily.
          </p>
        </li>
        <li>
          <span className="font-medium">Configure the Chat API</span> (Google Chat API → Manage → Configuration):
          <ul className="list-disc pl-5 mt-1 text-xs text-muted-foreground space-y-1">
            <li>App name + avatar: your choice</li>
            <li>Functionality: tick <strong>Receive 1:1 messages</strong> and <strong>Join spaces and group conversations</strong></li>
            <li>
              Connection settings: <strong>HTTP endpoint URL</strong>:
              {eventsUrl ? (
                <CopyableValue value={eventsUrl} className="mt-2" />
              ) : statusQuery.isLoading ? (
                <span className="ml-1 text-muted-foreground italic">Loading…</span>
              ) : (
                <span className="ml-1 text-muted-foreground italic">(available once Boo is running)</span>
              )}
            </li>
            <li>Authentication Audience: your <strong>Project Number</strong></li>
            <li>Visibility: <strong>Available to specific people and groups in {`{your domain}`}</strong></li>
          </ul>
        </li>
      </ol>

      <div className="border-t pt-4 space-y-3">
        <div className="text-sm font-medium">Paste credentials</div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`gchat-project-number-${container.id}`} className="text-xs">
            Project Number
          </Label>
          <Input
            id={`gchat-project-number-${container.id}`}
            value={projectNumber}
            onChange={(e) => setProjectNumber(e.target.value)}
            placeholder="514407472646"
            disabled={install.isPending}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={`gchat-sa-json-${container.id}`} className="text-xs">
              Service Account JSON
            </Label>
            <label
              className="text-xs text-muted-foreground hover:text-foreground cursor-pointer underline underline-offset-2"
              htmlFor={`gchat-sa-json-upload-${container.id}`}
            >
              Upload file instead
            </label>
            <input
              id={`gchat-sa-json-upload-${container.id}`}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              disabled={install.isPending}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleSaFileUpload(file);
                // Reset so picking the same file twice still fires onChange.
                e.target.value = "";
              }}
            />
          </div>
          <textarea
            id={`gchat-sa-json-${container.id}`}
            value={serviceAccountJson}
            onChange={(e) => {
              setUploadError(null);
              setServiceAccountJson(e.target.value);
            }}
            placeholder={`{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key": "...",\n  "client_email": "...@....iam.gserviceaccount.com",\n  ...\n}`}
            rows={8}
            disabled={install.isPending}
            className="border border-border rounded-md px-3 py-2 text-xs font-mono bg-background resize-y focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {uploadError ? (
            <p className="text-xs text-destructive">{uploadError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Paste the JSON or upload the file you downloaded from Google Cloud. Stored encrypted in your Boo's vault — never logged.
            </p>
          )}
        </div>
        {install.isError && <InlineError error={install.error} />}
        <Button
          size="sm"
          disabled={!canSubmit || install.isPending}
          onClick={() => install.mutate()}
        >
          {install.isPending && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
          Connect
        </Button>
      </div>
    </div>
  );
}
